import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  TextInput,
  Alert,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/lib/supabase/client';
import { Loader } from '@/components/Loader';

interface UserProfile {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

/**
 * Avatar component with fallback for failed image loads
 */
const UserAvatar = ({ user, size = 48 }: { user: UserProfile; size?: number }) => {
  const [imageError, setImageError] = useState(false);
  
  const initials = user.full_name?.charAt(0)?.toUpperCase() || '?';
  
  if (!user.avatar_url || imageError) {
    return (
      <View 
        style={{ width: size, height: size, borderRadius: size / 2 }}
        className="bg-purple-500 items-center justify-center"
      >
        <Text className="text-white font-bold" style={{ fontSize: size * 0.4 }}>
          {initials}
        </Text>
      </View>
    );
  }
  
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden' }}>
      <Image
        source={{ uri: user.avatar_url }}
        style={{ width: size, height: size }}
        contentFit="cover"
        onError={() => setImageError(true)}
      />
    </View>
  );
};

export default function NewGroupChatScreen() {
  const router = useRouter();
  const { user, activeProfile } = useAuth();
  // Use auth.uid for RLS policy compliance, but can use activeProfile for display
  const authUserId = user?.id;
  const displayUserId = activeProfile?.id || user?.id;

  const [step, setStep] = useState<'select' | 'details'>('select');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = users.filter((u) =>
        u.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .neq('id', displayUserId)
        .order('full_name');

      if (error) throw error;
      setUsers(data || []);
      setFilteredUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUser = (selectedUser: UserProfile) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === selectedUser.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== selectedUser.id);
      }
      return [...prev, selectedUser];
    });
  };

  const handleNext = () => {
    if (selectedUsers.length < 1) {
      Alert.alert('Select members', 'Please select at least 1 member for the group');
      return;
    }
    setStep('details');
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      Alert.alert('Group name required', 'Please enter a name for the group');
      return;
    }

    setCreating(true);
    try {
      // Get the current session directly from Supabase to ensure we have the correct auth.uid()
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.user?.id) {
        throw new Error('Not authenticated');
      }
      
      const currentAuthUserId = session.user.id;

      // Use RPC to create group chat with participants in one transaction
      // This avoids RLS issues with SELECT after INSERT
      const { data: chatId, error: rpcError } = await supabase.rpc('create_group_chat', {
        p_name: groupName.trim(),
        p_member_ids: selectedUsers.map(u => u.id),
      });

      if (rpcError) {
        // Fallback: Try manual creation if RPC doesn't exist
        if (rpcError.code === '42883') {
          // Function doesn't exist, use manual approach
          
          // Create the group chat without returning data
          const { error: chatError } = await supabase
            .from('chats')
            .insert({
              type: 'group',
              name: groupName.trim(),
              created_by: currentAuthUserId,
            });

          if (chatError) throw chatError;

          // Fetch the chat we just created
          const { data: newChat, error: fetchError } = await supabase
            .from('chats')
            .select('id')
            .eq('name', groupName.trim())
            .eq('created_by', currentAuthUserId)
            .eq('type', 'group')
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (fetchError) throw fetchError;

          // Add all participants (including creator)
          const participants = [
            { chat_id: newChat.id, user_id: currentAuthUserId, is_admin: true },
            ...selectedUsers.map((u) => ({
              chat_id: newChat.id,
              user_id: u.id,
              is_admin: false,
            })),
          ];

          const { error: participantsError } = await supabase
            .from('chat_participants')
            .insert(participants);

          if (participantsError) throw participantsError;

          // Navigate to the new group chat
          router.replace(`/chat-screens/${newChat.id}` as any);
          return;
        }
        throw rpcError;
      }

      // Navigate to the new group chat
      router.replace(`/chat-screens/${chatId}` as any);
    } catch (err) {
      console.error('Error creating group:', err);
      Alert.alert('Error', 'Failed to create group chat');
      setCreating(false);
    }
  };

  const renderUser = ({ item }: { item: UserProfile }) => {
    const isSelected = selectedUsers.some((u) => u.id === item.id);
    
    return (
      <TouchableOpacity
        onPress={() => handleToggleUser(item)}
        className={`flex-row items-center px-4 py-3 border-b border-gray-100 dark:border-gray-700 ${
          isSelected ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-white dark:bg-gray-800'
        }`}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: isSelected }}
        accessibilityLabel={`${item.full_name}, ${isSelected ? 'selected' : 'not selected'}`}
      >
        <UserAvatar user={item} size={48} />
        <Text className="ml-3 text-base font-medium text-gray-900 dark:text-white flex-1">
          {item.full_name || 'Unknown'}
        </Text>
        <View
          className={`w-6 h-6 rounded-full border-2 items-center justify-center ${
            isSelected
              ? 'bg-purple-600 border-purple-600'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          {isSelected && <Ionicons name="checkmark" size={16} color="white" />}
        </View>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <SafeAreaView className="flex-1 bg-gray-50 dark:bg-gray-900">
        <View className="flex-1 items-center justify-center">
          <Loader size={60} />
        </View>
      </SafeAreaView>
    );
  }

  // Step 2: Group details
  if (step === 'details') {
    return (
      <SafeAreaView edges={['top', 'bottom']} className="flex-1 bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <View className="bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <View className="flex-row items-center justify-between">
            <TouchableOpacity
              onPress={() => setStep('select')}
              className="p-2 -ml-2"
              accessibilityRole="button"
              accessibilityLabel="Go back"
            >
              <Ionicons name="arrow-back" size={24} color="#7C3AED" />
            </TouchableOpacity>
            <Text className="text-xl font-bold text-gray-900 dark:text-white">
              Group Details
            </Text>
            <TouchableOpacity
              onPress={handleCreateGroup}
              disabled={creating || !groupName.trim()}
              className="p-2"
              accessibilityRole="button"
              accessibilityLabel="Create group"
            >
              <Text
                className={`font-semibold ${
                  groupName.trim() ? 'text-purple-600' : 'text-gray-400'
                }`}
              >
                Create
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Creating overlay */}
        {creating && (
          <View className="absolute inset-0 bg-black/50 z-10 items-center justify-center">
            <View className="bg-white dark:bg-gray-800 rounded-lg p-6 items-center">
              <Loader size={40} />
              <Text className="mt-3 text-gray-600 dark:text-gray-300">
                Creating group...
              </Text>
            </View>
          </View>
        )}

        <ScrollView className="flex-1 px-4 py-6">
          {/* Group name input */}
          <View className="mb-6">
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              GROUP NAME
            </Text>
            <TextInput
              value={groupName}
              onChangeText={setGroupName}
              placeholder="Enter group name..."
              placeholderTextColor="#9CA3AF"
              className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700"
              autoFocus
            />
          </View>

          {/* Selected members */}
          <View>
            <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
              MEMBERS ({selectedUsers.length + 1})
            </Text>
            <View className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Current user (creator) */}
              <View className="flex-row items-center px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <View className="w-10 h-10 rounded-full bg-green-500 items-center justify-center">
                  <Ionicons name="person" size={20} color="white" />
                </View>
                <Text className="ml-3 text-base text-gray-900 dark:text-white flex-1">
                  You
                </Text>
                <Text className="text-xs text-green-500 font-medium">Admin</Text>
              </View>
              {/* Selected users */}
              {selectedUsers.map((u) => (
                <View
                  key={u.id}
                  className="flex-row items-center px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <UserAvatar user={u} size={40} />
                  <Text className="ml-3 text-base text-gray-900 dark:text-white flex-1">
                    {u.full_name}
                  </Text>
                  <TouchableOpacity
                    onPress={() => handleToggleUser(u)}
                    className="p-1"
                  >
                    <Ionicons name="close-circle" size={22} color="#EF4444" />
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // Step 1: Select members
  return (
    <SafeAreaView edges={['top', 'bottom']} className="flex-1 bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <View className="bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <View className="flex-row items-center justify-between">
          <TouchableOpacity
            onPress={() => router.back()}
            className="p-2 -ml-2"
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="arrow-back" size={24} color="#7C3AED" />
          </TouchableOpacity>
          <Text className="text-xl font-bold text-gray-900 dark:text-white">
            New Group
          </Text>
          <TouchableOpacity
            onPress={handleNext}
            disabled={selectedUsers.length < 1}
            className="p-2"
            accessibilityRole="button"
            accessibilityLabel="Next step"
          >
            <Text
              className={`font-semibold ${
                selectedUsers.length >= 1 ? 'text-purple-600' : 'text-gray-400'
              }`}
            >
              Next
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Selected users - circular avatars */}
      {selectedUsers.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"
          contentContainerStyle={{ padding: 16, gap: 12 }}
        >
          {selectedUsers.map((u) => (
            <TouchableOpacity
              key={u.id}
              onPress={() => handleToggleUser(u)}
              className="items-center"
              style={{ width: 70 }}
            >
              <View className="relative">
                <UserAvatar user={u} size={56} />
                <View className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gray-500 items-center justify-center">
                  <Ionicons name="close" size={12} color="white" />
                </View>
              </View>
              <Text 
                className="text-xs text-gray-600 dark:text-gray-400 mt-1 text-center"
                numberOfLines={1}
              >
                {u.full_name?.split(' ')[0] || 'User'}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      {/* Search */}
      <View className="px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <View className="flex-row items-center bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
          <Ionicons name="search" size={20} color="#9CA3AF" />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search users..."
            placeholderTextColor="#9CA3AF"
            className="flex-1 ml-2 text-gray-900 dark:text-white"
            autoCapitalize="none"
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#9CA3AF" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* User list */}
      <FlatList
        data={filteredUsers}
        keyExtractor={(item) => item.id}
        renderItem={renderUser}
        ListEmptyComponent={
          <View className="flex-1 items-center justify-center py-12">
            <Text className="text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No users found' : 'No users available'}
            </Text>
          </View>
        }
        contentContainerStyle={{ flexGrow: 1 }}
      />
    </SafeAreaView>
  );
}
